// Add CSS to prevent layout shifts
function addStabilityCSS() {
  if (!document.querySelector('#rebuy-stability-css')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'rebuy-stability-css';
    styleSheet.textContent = `
      [data-rebuy-component="cart-items"] {
        min-height: 200px;
        transition: min-height 0.3s ease;
      }
    `;
    document.head.appendChild(styleSheet);
  }
}

// Debounce function to prevent multiple rapid calls
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

// Global state management for timeouts, observers, and event listeners
const timeouts = {
  cartCheck: null,
  progressBarObserver: null,
  cartOperation: null
};

const observers = {
  progressBar: null
};

const eventListeners = {
  cart: [],
  sellingPlan: null,
  domContentLoaded: null,
  beforeUnload: null
};

// Cache DOM selectors for better performance
const DOM = {
  progressBar: null,
  progressStepWrapper: null,
  progressBarWrapper: null,
  subscriptionMessage: null,
  
  // Initialize DOM elements
  init() {
    this.progressBar = document.querySelector('[data-rebuy-component="progress-bar"]');
    if (this.progressBar) {
      this.progressStepWrapper = this.progressBar.querySelector('.rebuy-cart__progress-step-wrapper');
      this.progressBarWrapper = this.progressBar.querySelector('.rebuy-cart__progress-bar-wrapper');
      this.subscriptionMessage = this.progressBar.querySelector('.rebuy-cart__subscription-message');
    }
    return this.progressBar !== null;
  },
  
  // Refresh DOM elements
  refresh() {
    return this.init();
  }
};

// Centralized state management
const State = {
  isCartOperationInProgress: false,
  cartOperationQueue: [],
  isProcessingQueue: false,
  isSubscriptionFlowInProgress: false,
  
  // Reset all state
  reset() {
    this.isCartOperationInProgress = false;
    this.isProcessingQueue = false;
    this.isSubscriptionFlowInProgress = false;
  }
};

// Consolidated event handler for all cart events
function handleCartEvent(event) {
  const detail = event.detail || {};
  
  // Queue the operation instead of executing it directly
  queueCartOperation(() => {
    return checkForSubscriptionProducts(detail);
  });
  
  // Use a more adaptive timing approach
  // Start with a shorter delay and extend if needed
  if (timeouts.cartOperation) {
    clearTimeout(timeouts.cartOperation);
  }
  
  timeouts.cartOperation = setTimeout(() => {
    // Check if there are still pending operations
    if (State.cartOperationQueue.length > 0 || State.isProcessingQueue) {
      // If there are still operations, extend the timeout
      clearTimeout(timeouts.cartOperation);
      timeouts.cartOperation = setTimeout(() => {
        // No need to hide loader anymore
      }, 1000);
    }
  }, 300);
}

// Rebuy Cart Event Listeners - consolidated with debounce
const debouncedCartHandler = debounce(handleCartEvent, 300);

// Add event listeners for all Rebuy cart events
['rebuy:cart.ready', 'rebuy:cart.add', 'rebuy:cart.change', 'rebuy:cart.enriched'].forEach(eventName => {
  const listener = (event) => debouncedCartHandler(event);
  document.addEventListener(eventName, listener);
  eventListeners.cart.push({ name: eventName, listener });
});

// Add event listener for Rebuy selling plan changes
const sellingPlanListener = (event) => {
  const detail = event.detail || {};
  
  if (detail.selling_plan_id) {
    fetch('/cart.js')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch cart data');
        }
        return response.json();
      })
      .then(cart => {
        const hasSubscription = cart.items && cart.items.some(item => 
          item.selling_plan_allocation && 
          (item.selling_plan_allocation.selling_plan || item.selling_plan_allocation.selling_plan_id)
        );
        
        if (hasSubscription) {
          updateProgressBarForSubscription();
        }
      })
      .catch(error => {
        // Error handling without logging
      });
  }
};

document.addEventListener('rebuy:selling-plan.change', sellingPlanListener);
eventListeners.sellingPlan = sellingPlanListener;

// Check for subscription products on page load
const domContentLoadedListener = () => {
  addStabilityCSS();
  checkShopifyCart();
  setupProgressBarObserver();
};

document.addEventListener('DOMContentLoaded', domContentLoadedListener);
eventListeners.domContentLoaded = domContentLoadedListener;

// Set up a mutation observer to watch for the progress bar element
function setupProgressBarObserver() {
  // Clear any existing observer and timeout
  if (observers.progressBar) {
    observers.progressBar.disconnect();
    observers.progressBar = null;
  }
  
  if (timeouts.progressBarObserver) {
    clearTimeout(timeouts.progressBarObserver);
    timeouts.progressBarObserver = null;
  }
  
  let observerTimeout;
  const observer = new MutationObserver((mutations) => {
    // Clear any existing timeout to prevent multiple rapid checks
    clearTimeout(observerTimeout);
    
    // Set a new timeout to debounce the check
    observerTimeout = setTimeout(() => {
      if (DOM.refresh()) {
        observer.disconnect();
        observers.progressBar = null;
        checkShopifyCart();
      }
    }, 100);
  });
  
  observers.progressBar = observer;
  
  observer.observe(document.body, { 
    childList: true, 
    subtree: true 
  });
  
  // Check immediately and disconnect if progress bar is found
  if (DOM.refresh()) {
    observer.disconnect();
    observers.progressBar = null;
    checkShopifyCart();
  }
  
  // Set a timeout to disconnect the observer if it hasn't found the element after a reasonable time
  timeouts.progressBarObserver = setTimeout(() => {
    if (observers.progressBar) {
      observers.progressBar.disconnect();
      observers.progressBar = null;
    }
  }, 10000); // Disconnect after 10 seconds if element not found
}

// Check Shopify cart directly
function checkShopifyCart() {
  return fetch('/cart.js')
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch cart data');
      }
      return response.json();
    })
    .then(cart => {
      const hasSubscription = cart.items && cart.items.some(item => 
        item.selling_plan_allocation && 
        (item.selling_plan_allocation.selling_plan || item.selling_plan_allocation.selling_plan_id)
      );
      
      if (hasSubscription) {
        updateProgressBarForSubscription();
      } else {
        updateProgressBarForOneTime();
      }
      
      return { success: true, hasSubscription };
    })
    .catch(error => {
      // Fallback to one-time purchase UI
      updateProgressBarForOneTime();
      return { success: false, error };
    });
}

// Function to check if there are subscription products in the cart
function checkForSubscriptionProducts(detail) {
  return new Promise((resolve) => {
    if (!detail) {
      updateProgressBarForOneTime();
      resolve(false);
      return;
    }
    
    let cart;
    let itemToCheck;
    
    // Use optional chaining for safer property access
    if (detail?.item?.selling_plan_allocation) {
      itemToCheck = detail.item;
      cart = detail.cart || detail;
    } else if (detail?.cart && (detail.cart.items || typeof detail.cart.items === 'function')) {
      cart = detail.cart;
    } else {
      cart = detail;
    }
    
    if (itemToCheck) {
      const hasSubscription = itemToCheck.selling_plan_allocation && 
                             Boolean(itemToCheck.selling_plan_allocation.selling_plan || 
                                    itemToCheck.selling_plan_allocation.selling_plan_id);
      
      if (hasSubscription) {
        if (State.isSubscriptionFlowInProgress) {
          resolve(false);
          return;
        }
        
        State.isSubscriptionFlowInProgress = true;
        
        updateProgressBarForSubscription();
        
        resolve(true);
        
        setTimeout(() => {
          State.isSubscriptionFlowInProgress = false;
        }, 1000);
      } else {
        updateProgressBarForOneTime();
        resolve(false);
      }
    }
    
    if (!cart) {
      updateProgressBarForOneTime();
      resolve(false);
      return;
    }
    
    let items;
    
    if (typeof cart.items === 'function') {
      try {
        items = cart.items();
      } catch (error) {
        updateProgressBarForOneTime();
        resolve(false);
        return;
      }
    } else {
      items = cart.items;
    }
    
    if (!items) {
      updateProgressBarForOneTime();
      resolve(false);
      return;
    }
    
    if (!Array.isArray(items) && typeof items === 'object') {
      items = Object.values(items);
    }
    
    if (!Array.isArray(items)) {
      updateProgressBarForOneTime();
      resolve(false);
      return;
    }
    
    const hasSubscription = checkItemsForSubscriptions(items);
    
    if (hasSubscription) {
      if (State.isSubscriptionFlowInProgress) {
        resolve(false);
        return;
      }
      
      State.isSubscriptionFlowInProgress = true;
      
      updateProgressBarForSubscription();
      
      resolve(true);
      
      setTimeout(() => {
        State.isSubscriptionFlowInProgress = false;
      }, 1000);
    } else {
      updateProgressBarForOneTime();
      resolve(false);
    }
  });
}

// Helper function to check items for subscriptions
function checkItemsForSubscriptions(items) {
  if (!items || !Array.isArray(items)) {
    return false;
  }
  
  return items.some(item => 
    item && 
    item.selling_plan_allocation && 
    (item.selling_plan_allocation.selling_plan || item.selling_plan_allocation.selling_plan_id)
  );
}

// Function to update the progress bar for subscription
function updateProgressBarForSubscription() {
  if (!DOM.refresh()) {
    return;
  }
  
  if (!DOM.progressBar) {
    return;
  }
  
  DOM.progressBar.setAttribute('data-has-subscription-message', 'true');
  
  if (DOM.progressStepWrapper) {
    DOM.progressStepWrapper.style.display = 'none';
  }
  
  if (DOM.progressBarWrapper) {
    DOM.progressBarWrapper.style.display = 'none';
    
    // Check if subscription message already exists
    if (!DOM.subscriptionMessage) {
      // Check if there's already a subscription message element
      const existingMessage = DOM.progressBar.querySelector('.rebuy-cart__subscription-message');
      if (existingMessage) {
        DOM.subscriptionMessage = existingMessage;
      } else {
        // Create new subscription message element
        DOM.subscriptionMessage = document.createElement('div');
        DOM.subscriptionMessage.className = 'rebuy-cart__subscription-message';
        DOM.subscriptionMessage.style.width = '100%';
        DOM.subscriptionMessage.style.padding = '15px 0';
        DOM.subscriptionMessage.style.textAlign = 'center';
        DOM.subscriptionMessage.style.backgroundColor = '#F9F8F4';
        DOM.subscriptionMessage.style.borderRadius = '8px';
        DOM.subscriptionMessage.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
        
        // Check if badge already exists
        const existingBadge = DOM.subscriptionMessage.querySelector('.rebuy-cart__subscription-badge');
        let badge;
        
        if (existingBadge) {
          badge = existingBadge;
        } else {
          badge = document.createElement('div');
          badge.className = 'rebuy-cart__subscription-badge';
          badge.style.display = 'inline-flex';
          badge.style.alignItems = 'center';
          badge.style.justifyContent = 'center';
          badge.style.backgroundColor = '#e8f0e3';
          badge.style.color = '#2e7d32';
          badge.style.padding = '6px 12px';
          badge.style.borderRadius = '20px';
          badge.style.marginBottom = '10px';
          badge.style.fontWeight = '600';
          badge.style.fontSize = '14px';
          
          const giftIcon = document.createElement('span');
          giftIcon.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;">
              <polyline points="20 12 20 22 4 22 4 12"></polyline>
              <rect x="2" y="7" width="20" height="5"></rect>
              <line x1="12" y1="22" x2="12" y2="7"></line>
              <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
              <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
            </svg>
          `;
          badge.appendChild(giftIcon);
          
          const badgeText = document.createElement('span');
          badgeText.innerHTML = "Subscription Discount Applied";
          badge.appendChild(badgeText);
        }
        
        // Check if message already exists
        const existingMessageText = DOM.subscriptionMessage.querySelector('.rebuy-cart__subscription-message-text');
        let message;
        
        if (existingMessageText) {
          message = existingMessageText;
        } else {
          message = document.createElement('div');
          message.className = 'rebuy-cart__subscription-message-text';
          message.style.color = '#4a4a4a';
          message.style.fontWeight = '500';
          message.style.fontSize = '14px';
          message.style.lineHeight = '1.5';
          message.innerHTML = 'Thank you for subscribing to Forest Super Foods. Your subscription discount has been applied to your order.';
        }
        
        // Clear existing content and append new elements
        DOM.subscriptionMessage.innerHTML = '';
        DOM.subscriptionMessage.appendChild(badge);
        DOM.subscriptionMessage.appendChild(message);
        
        DOM.progressBar.appendChild(DOM.subscriptionMessage);
      }
    } else {
      DOM.subscriptionMessage.style.display = 'block';
    }
  }
}

// Function to restore progress bar for one-time purchase
function updateProgressBarForOneTime() {
  if (!DOM.refresh()) {
    return;
  }
  
  if (!DOM.progressBar) {
    return;
  }
  
  if (DOM.progressBar.getAttribute('data-has-subscription-message') === 'true') {
    if (DOM.progressStepWrapper) {
      DOM.progressStepWrapper.style.display = 'flex';
    }
    
    if (DOM.progressBarWrapper) {
      DOM.progressBarWrapper.style.display = 'block';
    }
    
    if (DOM.subscriptionMessage) {
      DOM.subscriptionMessage.style.display = 'none';
    }
    
    DOM.progressBar.removeAttribute('data-has-subscription-message');
  }
}

// Queue system for cart operations to prevent race conditions
function queueCartOperation(operation) {
  return new Promise((resolve, reject) => {
    // Add the operation to the queue
    State.cartOperationQueue.push({
      operation,
      resolve,
      reject
    });
    
    // Process the queue if not already processing
    processCartOperationQueue();
  });
}

// Process the cart operation queue
function processCartOperationQueue() {
  // If already processing or queue is empty, return
  if (State.isProcessingQueue || State.cartOperationQueue.length === 0) {
    return;
  }
  
  // Set processing flag
  State.isProcessingQueue = true;
  
  // Get the next operation
  const { operation, resolve, reject } = State.cartOperationQueue.shift();
  
  // Add a retry counter to prevent infinite loops
  let retryCount = 0;
  const maxRetries = 3;
  
  // Execute the operation with retry logic
  const executeOperation = () => {
    Promise.resolve(operation())
      .then(result => {
        resolve(result);
      })
      .catch(error => {
        // If we haven't exceeded max retries, try again
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(executeOperation, 1000 * retryCount); // Exponential backoff
        } else {
          // After max retries, reject the promise
          reject(new Error(`Operation failed after ${maxRetries} retries`));
        }
      })
      .finally(() => {
        // Reset processing flag
        State.isProcessingQueue = false;
        
        // Process next operation if any
        if (State.cartOperationQueue.length > 0) {
          setTimeout(processCartOperationQueue, 100);
        }
      });
  };
  
  // Start the execution
  executeOperation();
}

// Cleanup function to remove event listeners, disconnect observers, and clear timeouts
function cleanup() {
  // Remove event listeners
  eventListeners.cart.forEach(({ name, listener }) => {
    document.removeEventListener(name, listener);
  });
  
  if (eventListeners.sellingPlan) {
    document.removeEventListener('rebuy:selling-plan.change', eventListeners.sellingPlan);
  }
  
  if (eventListeners.domContentLoaded) {
    document.removeEventListener('DOMContentLoaded', eventListeners.domContentLoaded);
  }
  
  // Disconnect observers
  if (observers.progressBar) {
    observers.progressBar.disconnect();
    observers.progressBar = null;
  }
  
  // Clear timeouts
  Object.values(timeouts).forEach(timeout => {
    if (timeout) {
      clearTimeout(timeout);
    }
  });
}

// Add cleanup event listener
const beforeUnloadListener = () => {
  cleanup();
};

window.addEventListener('beforeunload', beforeUnloadListener);
eventListeners.beforeUnload = beforeUnloadListener;

// Function to check country and hide announcement bar if AU, NZ, or US
function checkCountryAndHideAnnouncement() {
  const announcementBar = document.querySelector('[data-rebuy-component="announcement-bar"]');
  if (!announcementBar) return;

  // Try multiple ways to get the country code
  let countryCode = null;
  // Method 1: Check the selected country in the localization selector
  const selectedCountry = document.querySelector('.popover-listbox__option[aria-selected="true"]');
  if (selectedCountry) {
    countryCode = selectedCountry.getAttribute('value');
  }
  // Method 2: Check the country selector value
  if (!countryCode) {
    const countrySelector = document.querySelector('.localization-selector__value');
    if (countrySelector) {
      countryCode = countrySelector.textContent.trim().split(' ')[0];
    }
  }
  // Method 3: Check the form input
  if (!countryCode) {
    const countryInput = document.querySelector('select[name="address[country]"]');
    if (countryInput) {
      countryCode = countryInput.value;
    }
  }
  if (["AU", "NZ", "US"].includes(countryCode)) {
    announcementBar.style.display = 'none';
  } else {
    announcementBar.style.display = '';
  }
}

// Add event listener for country changes
document.addEventListener('rebuy:cart.ready', () => {
  checkCountryAndHideAnnouncement();
});

// Add event listener for localization changes
document.addEventListener('localization:country-changed', () => {
  checkCountryAndHideAnnouncement();
});

// Also check on page load
document.addEventListener('DOMContentLoaded', () => {
  checkCountryAndHideAnnouncement();
});

// Add a mutation observer to watch for country changes
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes' && mutation.attributeName === 'aria-selected') {
      checkCountryAndHideAnnouncement();
    }
  });
});

// Start observing the country selector
const countrySelector = document.querySelector('.popover-listbox');
if (countrySelector) {
  observer.observe(countrySelector, {
    attributes: true,
    childList: true,
    subtree: true
  });
}

// MutationObserver to watch for the announcement bar being added
const rebuyAnnouncementObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'childList') {
      // Check if the announcement bar is now present
      const announcementBar = document.querySelector('[data-rebuy-component="announcement-bar"]');
      if (announcementBar) {
        console.log('Announcement bar detected by MutationObserver');
        checkCountryAndHideAnnouncement();
      }
    }
  });
});

// Start observing the body for added nodes
rebuyAnnouncementObserver.observe(document.body, { childList: true, subtree: true }); 